import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { FormField, useFieldIds } from "../FormField/FormField";

export interface MultiSelectOption {
  /** Lo que viaja en el formulario. */
  value: string;
  /** Lo que se lee en la opción. */
  label: string;
  /**
   * El renglón pequeño BAJO la etiqueta: qué pasa si esta opción está
   * puesta y qué pasa si no.
   *
   * Es la razón por la que este componente existe y no es un
   * `<select multiple>` — ver el bloque de abajo.
   */
  hint?: ReactNode;
  disabled?: boolean;
}

export interface MultiSelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  /** Su presencia pone el campo en rojo y enlaza `aria-invalid`. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  options: readonly MultiSelectOption[];
  /** Los valores elegidos. Controlado, siempre. */
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Lo que se lee en el campo cerrado cuando no hay nada elegido. */
  placeholder?: string;
  id?: string;
  className?: string;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Selección MÚLTIPLE: un campo que se abre, con ayuda por opción.
 *
 * ── Lo que pidió Anahí, y lo que había ──────────────────────────────
 * "Un select múltiple, o sea un input desplegable para elegir varias
 * opciones" (26-ago). Lo que había eran las cuatro opciones SIEMPRE a la
 * vista, cada una en su tarjeta con su casilla: cuatro renglones de alto
 * permanentes en un paso que ya iba justo, y una lista que no se lee como
 * un campo del formulario sino como una sección aparte.
 *
 * Ahora es un campo cerrado que resume lo elegido y se abre para cambiar
 * lo que haya. Cerrado ocupa lo que ocupa cualquier campo.
 *
 * ── Por qué no es un `<select multiple>` ────────────────────────────
 * El sistema usa controles NATIVOS siempre que puede: `Select` es un
 * `<select>` de verdad justamente para no reimplementar la rueda del
 * teclado y la accesibilidad. Aquí no se pudo, y la razón sigue siendo la
 * misma que cuando eran casillas: un `<option>` solo admite TEXTO. Estas
 * opciones necesitan cada una su explicación —"si está desactivado, el
 * producto no será visible en la tienda"— y esa explicación es la mitad
 * del control: sin ella, "Activo en catálogo" y "Disponible para comprar"
 * se leen como sinónimos y quien administra apaga la equivocada.
 *
 * Además, un `<select multiple>` nativo se maneja con Ctrl+clic, que casi
 * nadie sabe, y basta un clic suelto para borrar todo lo seleccionado.
 *
 * ── El resumen dice QUÉ, no CUÁNTAS ─────────────────────────────────
 * "Activo en catálogo, +2" se entiende sin abrir el campo; "3
 * seleccionadas" obliga a abrirlo para saber si está puesta la que
 * importa. Y lo abreviado es SOLO lo visible: el campo lleva además la
 * lista entera en un texto para lector de pantalla, porque quien la oye
 * no tiene la salida de abrirlo para ver el resto.
 *
 * ── Lo que se implementa a mano, y con qué ──────────────────────────
 * El patrón ARIA de combobox con `listbox` desplegado:
 *
 *  · El campo es un `<button>` con `aria-haspopup="listbox"`,
 *    `aria-expanded` y `aria-controls`.
 *  · Se abre con Enter, Espacio, ↓ o ↑ — y ↓/↑ dejan el foco ya dentro.
 *  · Dentro: ↑ ↓ mueven, Inicio y Fin saltan a los extremos, Espacio y
 *    Enter alternan la opción, y la lista NO se cierra al marcar: en una
 *    selección múltiple, cerrarse tras cada clic obliga a reabrir por
 *    cada opción.
 *  · Escape cierra y DEVUELVE EL FOCO al campo, que es lo que evita que
 *    el tabulador reaparezca al principio del formulario.
 *  · Un clic fuera cierra sin tocar lo elegido.
 *  · `aria-selected` en cada opción, que es lo que un lector anuncia, y
 *    la ayuda enlazada por `aria-describedby` para que se lea AL LLEGAR.
 *
 * El foco lo llevan las opciones con tabindex móvil: dentro de la lista
 * solo una es alcanzable con el tabulador y el resto se recorre con
 * flechas, que es el patrón de ARIA para una lista.
 * ═══════════════════════════════════════════════════════════════════
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  function MultiSelect(
    {
      label,
      hint,
      error,
      required,
      disabled,
      options,
      value,
      onChange,
      placeholder,
      id,
      className,
    },
    ref,
  ) {
    const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
      hasHint: Boolean(hint),
      hasError: Boolean(error),
    });

    const listId = useId();
    const [open, setOpen] = useState(false);

    const selected = new Set(value);
    const enabled = options.filter((option) => !option.disabled);
    const chosen = options.filter((option) => selected.has(option.value));

    /*
     * El índice con el tabulador dentro de la lista. Es una `ref` y no
     * estado a propósito: mover el cursor no cambia NADA de lo que se ve
     * más allá del foco, y con estado cada flecha repintaría el paso.
     */
    const activeIndex = useRef(0);
    const listRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    const firstSelected = options.findIndex((option) => selected.has(option.value));

    /**
     * Lo que se lee en el campo cerrado.
     *
     * El primero por su nombre y el resto contados: "Activo en catálogo,
     * +2". Poner los cuatro nombres desbordaría el campo y cortaría el
     * último a media palabra, que es peor que resumir a propósito.
     */
    const summary =
      chosen.length === 0
        ? (placeholder ?? "Sin elegir")
        : chosen.length === 1
          ? (chosen[0] as MultiSelectOption).label
          : `${(chosen[0] as MultiSelectOption).label}, +${chosen.length - 1}`;

    /** La lista COMPLETA, para quien no ve el campo. */
    const spokenSummary =
      chosen.length === 0
        ? (placeholder ?? "Sin elegir")
        : chosen.map((option) => option.label).join(", ");

    /*
     * Un clic FUERA cierra. Se escucha en `pointerdown` y no en `click`
     * porque un clic sobre otro campo tiene que cerrar esta lista ANTES
     * de que el otro campo tome el foco: al revés, el foco salta y vuelve.
     */
    useEffect(() => {
      if (!open) return;
      function handlePointerDown(event: PointerEvent): void {
        const target = event.target as Node | null;
        if (target && wrapRef.current && !wrapRef.current.contains(target)) {
          setOpen(false);
        }
      }
      document.addEventListener("pointerdown", handlePointerDown);
      return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [open]);

    function focusOption(index: number): void {
      const clamped = Math.max(0, Math.min(index, options.length - 1));
      activeIndex.current = clamped;
      const node = listRef.current?.querySelectorAll<HTMLElement>("[role='option']")[clamped];
      node?.focus();
    }

    /** Abre y, si se pide, deja el cursor ya dentro de la lista. */
    function openList(focusInside: boolean): void {
      if (disabled) return;
      setOpen(true);
      if (!focusInside) return;
      const start = firstSelected >= 0 ? firstSelected : 0;
      // Tras el repintado: la lista todavía no existe en este instante.
      requestAnimationFrame(() => focusOption(start));
    }

    /** Cierra y devuelve el foco al campo, siempre por la misma puerta. */
    function closeList(): void {
      setOpen(false);
      buttonRef.current?.focus();
    }

    function toggle(option: MultiSelectOption): void {
      if (disabled || option.disabled) return;
      const next = selected.has(option.value)
        ? value.filter((item) => item !== option.value)
        : [...value, option.value];
      onChange(next);
    }

    /** Salta las deshabilitadas: aterrizar en una que no se puede pulsar es un callejón. */
    function step(from: number, direction: 1 | -1): number {
      let index = from + direction;
      while (index >= 0 && index < options.length && options[index]?.disabled) {
        index += direction;
      }
      return index >= 0 && index < options.length ? index : from;
    }

    function handleButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        openList(true);
        return;
      }
      if (event.key === "Escape" && open) {
        event.preventDefault();
        setOpen(false);
      }
    }

    function handleOptionKeyDown(
      event: KeyboardEvent<HTMLDivElement>,
      index: number,
    ): void {
      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          event.preventDefault();
          focusOption(step(index, 1));
          break;
        case "ArrowUp":
        case "ArrowLeft":
          event.preventDefault();
          focusOption(step(index, -1));
          break;
        case "Home":
          event.preventDefault();
          focusOption(enabled.length > 0 ? options.indexOf(enabled[0] as MultiSelectOption) : 0);
          break;
        case "End":
          event.preventDefault();
          focusOption(
            enabled.length > 0
              ? options.indexOf(enabled[enabled.length - 1] as MultiSelectOption)
              : options.length - 1,
          );
          break;
        case " ":
        case "Enter": {
          event.preventDefault();
          const option = options[index];
          // NO se cierra al marcar: en una selección múltiple, cerrarse
          // tras cada elección obliga a reabrir por cada opción.
          if (option) toggle(option);
          break;
        }
        case "Escape":
          event.preventDefault();
          closeList();
          break;
        case "Tab":
          // Tabular fuera cierra, pero sin robar el foco de vuelta: quien
          // tabula quiere seguir adelante, no volver al campo.
          setOpen(false);
          break;
        default:
          break;
      }
    }

    return (
      <FormField
        label={label}
        hint={hint}
        error={error}
        required={required}
        controlId={controlId}
        hintId={hintId}
        errorId={errorId}
        className={className}
      >
        <div ref={wrapRef} className="relative">
          <button
            ref={(node) => {
              buttonRef.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            type="button"
            id={controlId}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            disabled={disabled}
            onClick={() => (open ? setOpen(false) : openList(false))}
            onKeyDown={handleButtonKeyDown}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-xl border bg-ds-surface px-3 py-2 text-left transition",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50",
              error ? "border-ds-terracotta" : "border-ds-border-strong",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            {/*
              El resumen VISIBLE se abrevia; el hablado no. Quien ve la
              pantalla puede abrir el campo para ver el resto; quien lo
              oye no tiene esa salida, así que oye la lista entera.
            */}
            <span
              aria-hidden="true"
              className={cn(
                "truncate text-[13px]",
                chosen.length === 0 ? "text-ds-ink-3" : "text-ds-ink",
              )}
            >
              {summary}
            </span>
            <span className="sr-only">{spokenSummary}</span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "h-4 w-4 shrink-0 text-ds-ink-3 transition-transform",
                open && "rotate-180",
              )}
            />
          </button>

          {open && (
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby={controlId}
              /*
                Escape se atiende también AQUÍ, no solo en cada opción.
                El foco puede estar en el contenedor —al abrir con el
                ratón, antes de tocar una flecha— y en ese estado la
                tecla no llegaría a ninguna opción: la lista se quedaría
                abierta y quien la abrió pensaría que Escape no funciona.
              */
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                closeList();
              }}
              /*
                `absolute` y no en el flujo: si la lista empujara lo de
                abajo, abrirla movería medio paso de sitio y el clic
                siguiente caería sobre otro campo. `z-20` la deja por
                encima de las tarjetas vecinas.
              */
              className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 grid max-h-72 gap-1 overflow-y-auto rounded-2xl border border-ds-border bg-ds-surface p-1.5 shadow-ds-lift"
            >
              {options.map((option, index) => {
                const isSelected = selected.has(option.value);
                const optionHintId = option.hint
                  ? `${controlId}-${option.value}-hint`
                  : undefined;

                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    aria-describedby={optionHintId}
                    tabIndex={index === activeIndex.current ? 0 : -1}
                    onFocus={() => {
                      activeIndex.current = index;
                    }}
                    onKeyDown={(event) => handleOptionKeyDown(event, index)}
                    onClick={() => toggle(option)}
                    className={cn(
                      "flex cursor-pointer items-start gap-2.5 rounded-xl px-3 py-2 transition",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50",
                      isSelected ? "bg-ds-lavender-bg-2/40" : "hover:bg-ds-cream-2/60",
                      option.disabled && "cursor-not-allowed opacity-60",
                    )}
                  >
                    {/*
                      La marca es DECORATIVA: quien no la ve ya sabe si la
                      opción está puesta porque `aria-selected` se lo dice.
                      Un icono anunciado además sería la misma información
                      dos veces.

                      Y no es solo la marca: la opción elegida cambia
                      también de fondo y de peso de letra. Quien no
                      distingue el lila del crema tiene otras dos señales.
                    */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition",
                        isSelected
                          ? "border-ds-violet-pop bg-ds-violet-pop text-white"
                          : "border-ds-border-strong bg-ds-surface",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>

                    <span className="grid gap-0.5">
                      <span
                        className={cn(
                          "text-[12px] leading-snug",
                          isSelected ? "font-semibold text-ds-ink" : "text-ds-ink-2",
                        )}
                      >
                        {option.label}
                      </span>
                      {option.hint && (
                        /*
                          `ds-ink-3` y no `ds-ink-4` ni `ds-warm-gray`: los
                          dos últimos se quedan por debajo del 4.5:1 que
                          necesita un texto de este tamaño, y esta línea es
                          justo la que explica qué se apaga al desmarcar.
                          Ilegible, la opción vuelve a ser una casilla sin
                          contexto.
                        */
                        <span
                          id={optionHintId}
                          className="text-[11px] leading-relaxed text-ds-ink-3"
                        >
                          {option.hint}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </FormField>
    );
  },
);
